import * as Sentry from '@sentry/react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { reportError } from "@/utils/reportError";
import { getYesterdayCH } from '@/utils/date';
import i18n from '@/i18n';
import { supabase } from '@/utils/supabase';
import Constants from 'expo-constants';
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import { router } from 'expo-router';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { getNotificationRoute } from '@/utils/notificationRoute';

// `expo-notifications` does not provide web push support. Keep all native
// behavior intact while ensuring the web release never asks for a token it
// cannot receive.
if (Platform.OS !== 'web') {
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowAlert: true,
      shouldPlaySound: true,
      shouldSetBadge: true,
      shouldShowBanner: true,
      shouldShowList: true,
    }),
  });
}

export async function registerForPushNotificationsAsync(): Promise<string | null> {
  if (Platform.OS === 'web') return null;

  let token: string | null = null;

  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('default', {
      name: 'default',
      importance: Notifications.AndroidImportance.MAX,
      vibrationPattern: [0, 250, 250, 250],
      lightColor: '#FF231F7C',
    });
  }

  if (!Device.isDevice) {
    // Push notifications only work on physical devices
    return null;
  }

  const { status: existingStatus } = await Notifications.getPermissionsAsync();
  let finalStatus = existingStatus;
  
  if (existingStatus !== 'granted') {
    const { status } = await Notifications.requestPermissionsAsync();
    finalStatus = status;
  }
  
  if (finalStatus !== 'granted') {
    // Permission not granted for push notifications
    return null;
  }

  try {
    const projectId = Constants.expoConfig?.extra?.eas?.projectId;
    
    if (!projectId) {
      Sentry.captureMessage('Expo Project ID not found in app.json');
      return null;
    }

    token = (await Notifications.getExpoPushTokenAsync({ projectId })).data;
  } catch (error) {
    reportError(error);
  }

  return token;
}

// Identifiant d'installation STABLE et persistant. `Constants.deviceId`
// n'existe plus (expo-constants 18) → l'ancien fallback `device-${Date.now()}`
// changeait à CHAQUE lancement, donc l'upsert ne dédupliquait jamais : une
// nouvelle ligne push_tokens (active) par démarrage → notifications en double.
// On génère donc un id une seule fois et on le persiste.
const DEVICE_ID_KEY = 'push_device_id_v1';

async function getStableDeviceId(fallback: string): Promise<string> {
  try {
    let id = await AsyncStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      await AsyncStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    // Stockage indisponible : le token push est stable par installation,
    // on l'utilise pour rester idempotent plutôt qu'un id volatil.
    return fallback;
  }
}

/** Langue de notification pour cet appareil : langue i18next courante,
 * tronquée à 'fr' | 'en' (fallback 'fr'). Utilisée par les edge functions
 * send-* pour choisir la langue des notifications push. */
function currentPushLanguage(): 'fr' | 'en' {
  return i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'fr';
}

export async function savePushTokenToDatabase(token: string) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) {
      return { success: false, error: 'No user logged in' };
    }

    const deviceId = await getStableDeviceId(token);
    const platform = Platform.OS;


    // RPC SECURITY DEFINER : détache le token de tout autre propriétaire
    // (appareil partagé / changement de compte) puis le (ré)assigne à
    // l'utilisateur courant. Évite la violation de la contrainte unique
    // expo_push_token que l'upsert client ne pouvait pas résoudre (RLS).
    const { error } = await supabase.rpc('register_push_token', {
      p_token: token,
      p_device_id: deviceId,
      p_platform: platform,
      p_language: currentPushLanguage(),
    });

    if (error) {
      reportError(error);
      return { success: false, error: error.message };
    } else {
      return { success: true };
    }
  } catch (error: any) {
    reportError(error);
    return { success: false, error: error.message };
  }
}

/**
 * `Notifications.useLastNotificationResponse()` appelle
 * `getLastNotificationResponse` dans un layout effect : sur le web cette
 * méthode n'existe pas et crash (Sentry : ExpoNotifications.getLastNotificationResponse
 * is not available on web). Même contrat que le hook Expo, sans l'appeler.
 */
function useLastNotificationResponseSafe() {
  const [lastNotificationResponse, setLastNotificationResponse] = useState<
    Notifications.NotificationResponse | null | undefined
  >(() => (Platform.OS === 'web' ? null : undefined));

  useLayoutEffect(() => {
    if (Platform.OS === 'web') return;

    const response = Notifications.getLastNotificationResponse();
    setLastNotificationResponse((prev) => nextNotificationResponse(prev, response));

    const received = Notifications.addNotificationResponseReceivedListener((next) => {
      setLastNotificationResponse((prev) => nextNotificationResponse(prev, next));
    });
    const cleared = Notifications.addNotificationResponseClearedListener(() => {
      setLastNotificationResponse(null);
    });
    return () => {
      received.remove();
      cleared.remove();
    };
  }, []);

  return lastNotificationResponse;
}

function nextNotificationResponse(
  prev: Notifications.NotificationResponse | null | undefined,
  next: Notifications.NotificationResponse | null
) {
  if (!next) return null;
  if (!prev) return next;
  return prev.notification.request.identifier !== next.notification.request.identifier
    ? next
    : prev;
}

export function usePushNotifications(appReady = false) {
  const [expoPushToken, setExpoPushToken] = useState<string | null>(null);
  const [notification, setNotification] = useState<Notifications.Notification | null>(null);
  const notificationListener = useRef<Notifications.Subscription | null>(null);
  const handledResponseId = useRef<string | null>(null);
  const lastResponse = useLastNotificationResponseSafe();

  useEffect(() => {
    if (Platform.OS === 'web') return;

    const register = async () => {
      // Check existing permissions first without requesting new ones
      const { status: existingStatus } = await Notifications.getPermissionsAsync();
      
    
      if (existingStatus === 'granted') {
        const token = await registerForPushNotificationsAsync();
        if (token) {
          setExpoPushToken(token);
          await savePushTokenToDatabase(token);
        }
      }
    };

    register();

    // Listen for Auth changes to retry registration after login
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        // Same check here: only register if we already have permission
        const { status: existingStatus } = await Notifications.getPermissionsAsync();
        if (existingStatus === 'granted') {
           await register();
        }
      }
    });

    // Listener pour les notifications reçues pendant que l'app est ouverte
    notificationListener.current = Notifications.addNotificationReceivedListener(notification => {
      setNotification(notification);
    });

    // Changement de langue dans l'app → répercuter sur push_tokens pour que
    // les prochaines notifications serveur partent dans la bonne langue.
    const onLanguageChanged = () => {
      updatePushTokenLanguageForDevice();
    };
    i18n.on('languageChanged', onLanguageChanged);

    return () => {
      authListener.subscription.unsubscribe();
      i18n.off('languageChanged', onLanguageChanged);
      if (notificationListener.current) {
        notificationListener.current.remove();
      }
    };
  }, []);

  // Le badge / le tiroir Android ne doivent pas être vidés AVANT d'avoir lu le
  // tap : sur certains appareils, dismissAll pendant le cold start avale
  // l'intent et l'activité se ferme (l'app « ne s'ouvre pas »).
  useEffect(() => {
    if (Platform.OS === 'web' || !appReady) return;
    if (lastResponse === undefined) return;

    const clearAppBadge = async () => {
      try {
        await Notifications.setBadgeCountAsync(0);
        await Notifications.dismissAllNotificationsAsync();
      } catch {
        // non-bloquant
      }
    };

    if (lastResponse) {
      const id = lastResponse.notification.request.identifier;
      if (handledResponseId.current !== id) {
        handledResponseId.current = id;
        handleNotificationNavigation(lastResponse);
        logNotificationInteraction(lastResponse.notification);
        Notifications.clearLastNotificationResponseAsync().catch(() => undefined);
      }
    }

    clearAppBadge();
    const appStateSub = AppState.addEventListener('change', (state) => {
      if (state === 'active') clearAppBadge();
    });
    return () => appStateSub.remove();
  }, [appReady, lastResponse]);

  return { expoPushToken, notification };
}

async function logNotificationInteraction(notification: Notifications.Notification) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return;

    const content = notification.request.content;
    const type = (content.data?.type as string) || 'unknown';

    // On cible la notif la plus récente de ce type par son id : un UPDATE direct
    // avec .order().limit() n'est pas fiable côté PostgREST. La borne basse
    // (hier minuit UTC) couvre largement le décalage UTC/Zurich qui faisait
    // rater les notifs du soir suisse quand on comparait à 'YYYY-MM-DD'.
    const since = `${getYesterdayCH()}T00:00:00Z`;
    const { data: rows } = await supabase
      .from('notification_history')
      .select('id')
      .eq('user_id', user.id)
      .eq('notification_type', type)
      .gte('sent_at', since)
      .order('sent_at', { ascending: false })
      .limit(1);

    if (rows && rows.length > 0) {
      await supabase
        .from('notification_history')
        .update({ status: 'clicked' })
        .eq('id', rows[0].id);
    }
  } catch (error) {
    reportError(error);
  }
}

/** Navigation à partir d'une interaction notification (tap). */
function handleNotificationNavigation(
  response: Notifications.NotificationResponse | null
) {
  if (!response) return;
  const route = getNotificationRoute(response.notification?.request?.content?.data);
  const go = () => {
    try {
      router.replace(route);
    } catch {
      // Root Layout pas encore prêt : un retry court évite un crash silencieux
      // qui laissait l'app bloquée sur le splash.
      setTimeout(() => {
        try {
          router.replace(route);
        } catch {
          // L'index.tsx prendra le relais.
        }
      }, 400);
    }
  };
  go();
}

/** Met à jour la langue de notification du token push de CET appareil
 * (appelé quand l'utilisateur change la langue de l'app). Best-effort :
 * silencieux si pas de session ou pas de token enregistré. */
export async function updatePushTokenLanguageForDevice() {
  try {
    const deviceId = await getStableDeviceId('');
    if (!deviceId) return;
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return;
    await supabase
      .from('push_tokens')
      .update({ language: currentPushLanguage(), updated_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .eq('device_id', deviceId);
  } catch (error) {
    reportError(error);
  }
}

/** Désactive le(s) token(s) push de CET appareil. À appeler AVANT signOut
 * (tant que la session existe, sinon la RLS bloque l'update) pour ne plus
 * recevoir de notifications après déconnexion. */
export async function deactivatePushTokensForDevice() {
  try {
    const deviceId = await getStableDeviceId('');
    if (!deviceId) return;
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return;
    await supabase
      .from('push_tokens')
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .eq('device_id', deviceId);
  } catch (error) {
    reportError(error);
  }
}

// Fonction utilitaire pour envoyer une notification de test
export async function sendTestNotification() {
  if (Platform.OS === 'web') return;
  await Notifications.scheduleNotificationAsync({
    content: {
      title: i18n.t('notifications.testPushTitle'),
      body: i18n.t('notifications.testPushBody'),
      data: { screen: 'challenge' },
    },
    trigger: {
      type: Notifications.SchedulableTriggerInputTypes.TIME_INTERVAL,
      seconds: 2,
    },
  });
}
